/**
 * splitBioToParagraphs(rawBio: string, opts?: { sentencesPerPara?: number })
 *
 * Some artist biographies in the DB are a single 1,000+ character paragraph
 * (e.g. Banksy: 758 chars, 0 newlines). This helper:
 *
 *   1. Respects existing paragraph breaks (`\n\n` or `\n`) when present.
 *   2. If the supplied bio is "squashed" (no double-newlines, or one
 *      newline per >600 characters), re-paragraphizes it deterministically
 *      by grouping every ~3 sentences. Sentence boundaries are detected
 *      with a conservative regex that ignores `Mr.`, `Mrs.`, initials, and
 *      decimals so we don't split mid-sentence.
 *
 * Returns: string[] — never empty for non-blank input.
 */

const ABBREVIATIONS = new Set([
  'mr', 'mrs', 'ms', 'dr', 'prof', 'sr', 'jr', 'st', 'no',
  'vs', 'etc', 'e.g', 'i.e', 'cf', 'al', 'ibid', 'op',
]);

const splitIntoSentences = (text) => {
  // Insert a sentinel between any period/!/? followed by whitespace + capital
  // letter (or opening quote) — unless the trailing word is an abbreviation
  // or it looks like a decimal number (e.g. "1.5 million").
  const sentences = [];
  let buffer = '';
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    buffer += ch;
    if (ch === '.' || ch === '!' || ch === '?') {
      const next = text[i + 1];
      const after = text[i + 2];
      const isEndOfText = next === undefined;
      // Boundary candidate: punctuation followed by space + capital/quote
      const followsBoundary =
        next === ' ' &&
        after !== undefined &&
        (/[A-Z"'“‘(]/.test(after));
      if (isEndOfText || followsBoundary) {
        // Check the previous token for known abbreviations / initials
        const tail = buffer.slice(-6).match(/([A-Za-z.]+)\.$/);
        const word = tail ? tail[1].toLowerCase().replace(/\./g, '') : '';
        const isAbbrev = word.length <= 4 && ABBREVIATIONS.has(word);
        const isInitial = word.length === 1; // single letter -> "J. F. Kennedy"
        // Don't break on decimal numbers e.g. "1.5"
        const prev = buffer.slice(-2, -1);
        const isDecimal = /[0-9]/.test(prev) && /[0-9]/.test(next || '');
        if (!isAbbrev && !isInitial && !isDecimal) {
          sentences.push(buffer.trim());
          buffer = '';
        }
      }
    }
  }
  if (buffer.trim()) sentences.push(buffer.trim());
  return sentences;
};

const groupSentences = (sentences, perPara = 3) => {
  const out = [];
  for (let i = 0; i < sentences.length; i += perPara) {
    out.push(sentences.slice(i, i + perPara).join(' '));
  }
  return out;
};

// Tune paragraph density to bio length: ~4 sentences for longer bios,
// ~2 for very short ones, ~3 for everything in between. Extracted from
// the inline ternary so the rule is obvious at a glance and the
// thresholds are easier to adjust without re-parsing nested `?:`.
const paragraphDensity = (sentenceCount) => {
  if (sentenceCount >= 14) return 4;
  if (sentenceCount <= 4)  return 2;
  return 3;
};

export const splitBioToParagraphs = (rawBio, opts = {}) => {
  const text = (rawBio || '').trim();
  if (!text) return [];

  // Honour explicit author paragraphing if it looks intentional.
  const explicit = text.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
  if (explicit.length > 1) return explicit;

  const singleLineSplit = text.split(/\n+/).map((s) => s.trim()).filter(Boolean);
  // Treat as already-paragraphed when there's at least one newline per 600
  // chars (i.e. roughly authored breaks rather than a single squashed wall).
  if (singleLineSplit.length > 1 && text.length / singleLineSplit.length < 600) {
    return singleLineSplit;
  }

  const sentences = splitIntoSentences(text);
  if (sentences.length <= 1) return [text];

  const perPara = opts.sentencesPerPara || paragraphDensity(sentences.length);
  return groupSentences(sentences, perPara);
};

export default splitBioToParagraphs;
